import React, { useState, useEffect, useCallback } from 'react';
import { Copy, Download, Upload, RotateCcw, Maximize2, Minimize2, BarChart3, Globe, Zap, CheckCircle2 } from 'lucide-react';

interface ProcessingStats {
  charactersProcessed: number;
  entitiesFound: number;
  processingTime: number;
  operationsCount: number;
}

interface ProcessingResult {
  result: string;
  stats: Omit<ProcessingStats, 'operationsCount'>;
}

const UrlEscapeUnescapeTool: React.FC = () => {
  const [inputText, setInputText] = useState<string>('');
  const [outputText, setOutputText] = useState<string>('');
  const [mode, setMode] = useState<'escape' | 'unescape'>('escape');
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [showAnalytics, setShowAnalytics] = useState<boolean>(false);
  const [stats, setStats] = useState<ProcessingStats>({
    charactersProcessed: 0,
    entitiesFound: 0,
    processingTime: 0,
    operationsCount: 0
  });
  const [notification, setNotification] = useState<string>('');

  const escapeUrl = useCallback((text: string): ProcessingResult => {
    const startTime = performance.now();
    let result = text;
    let entitiesCount = 0;

    // URL encoding map for special characters
    const urlEncodeMap: Record<string, string> = {
      ' ': '%20',   // Space
      '!': '%21',   // Exclamation mark
      '"': '%22',   // Quotation mark
      '#': '%23',   // Hash/Fragment
      '$': '%24',   // Dollar sign
      '%': '%25',   // Percent (must be first to avoid double encoding)
      '&': '%26',   // Ampersand
      "'": '%27',   // Apostrophe
      '(': '%28',   // Left parenthesis
      ')': '%29',   // Right parenthesis
      '*': '%2A',   // Asterisk
      '+': '%2B',   // Plus sign
      ',': '%2C',   // Comma
      '/': '%2F',   // Forward slash
      ':': '%3A',   // Colon
      ';': '%3B',   // Semicolon
      '<': '%3C',   // Less than
      '=': '%3D',   // Equals
      '>': '%3E',   // Greater than
      '?': '%3F',   // Question mark
      '@': '%40',   // At sign
      '[': '%5B',   // Left square bracket
      '\\': '%5C',  // Backslash
      ']': '%5D',   // Right square bracket
      '^': '%5E',   // Caret
      '`': '%60',   // Backtick
      '{': '%7B',   // Left curly brace
      '|': '%7C',   // Pipe
      '}': '%7D',   // Right curly brace
      '~': '%7E',   // Tilde
    };

    // Process percent signs first to avoid double encoding
    const percentMatches = result.match(/%/g);
    if (percentMatches) {
      entitiesCount += percentMatches.length;
      result = result.replace(/%/g, '%25');
    }

    // Process other characters
    Object.entries(urlEncodeMap).forEach(([char, encoded]) => {
      if (char !== '%') { // Skip percent as it's already handled
        const regex = new RegExp(char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
        const matches = result.match(regex);
        if (matches) {
          entitiesCount += matches.length;
          result = result.replace(regex, encoded);
        }
      }
    });

    // Handle Unicode characters (non-ASCII)
    result = result.replace(/[^\x00-\x7F]/g, (match) => {
      entitiesCount++;
      return encodeURIComponent(match);
    });

    // Handle control characters
    result = result.replace(/[\x00-\x1F\x7F]/g, (match) => {
      const code = match.charCodeAt(0);
      entitiesCount++;
      return `%${code.toString(16).padStart(2, '0').toUpperCase()}`;
    });

    const endTime = performance.now();
    return {
      result,
      stats: {
        charactersProcessed: text.length,
        entitiesFound: entitiesCount,
        processingTime: endTime - startTime
      }
    };
  }, []);

  const unescapeUrl = useCallback((text: string): ProcessingResult => {
    const startTime = performance.now();
    let result = text;
    let entitiesCount = 0;

    // Decode percent-encoded sequences (%XX)
    result = result.replace(/%([0-9A-Fa-f]{2})/g, (match, hex) => {
      const code = parseInt(hex, 16);
      if (code >= 0 && code <= 255) {
        entitiesCount++;
        return String.fromCharCode(code);
      }
      return match; // Return original if invalid
    });

    // Handle plus signs as spaces (application/x-www-form-urlencoded)
    const plusMatches = result.match(/\+/g);
    if (plusMatches) {
      entitiesCount += plusMatches.length;
      result = result.replace(/\+/g, ' ');
    }

    // Use built-in decodeURIComponent for complex Unicode sequences
    try {
      const decoded = decodeURIComponent(result);
      if (decoded !== result) {
        // Count additional entities that were decoded
        const additionalDecoded = (result.match(/%[0-9A-Fa-f]{2}/g) || []).length;
        entitiesCount += additionalDecoded;
        result = decoded;
      }
    } catch (e) {
      // If decodeURIComponent fails, continue with our manual decoding
    }

    const endTime = performance.now();
    return {
      result,
      stats: {
        charactersProcessed: text.length,
        entitiesFound: entitiesCount,
        processingTime: endTime - startTime
      }
    };
  }, []);

  const processText = useCallback((): void => {
    if (!inputText.trim()) {
      setOutputText('');
      return;
    }

    const processor = mode === 'escape' ? escapeUrl : unescapeUrl;
    const { result, stats: processingStats } = processor(inputText);
    
    setOutputText(result);
    setStats(prev => ({
      ...processingStats,
      operationsCount: prev.operationsCount + 1
    }));
  }, [inputText, mode, escapeUrl, unescapeUrl]);

  useEffect(() => {
    processText();
  }, [processText]);

  const showNotification = (message: string): void => {
    setNotification(message);
    setTimeout(() => setNotification(''), 3000);
  };

  const copyToClipboard = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(outputText);
      showNotification('Copied to clipboard!');
    } catch (err) {
      showNotification('Failed to copy');
    }
  };

  const downloadFile = (): void => {
    const blob = new Blob([outputText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${mode}d-urls.txt`;
    a.click();
    URL.revokeObjectURL(url);
    showNotification('File downloaded!');
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result;
        if (typeof result === 'string') {
          setInputText(result);
          showNotification('File loaded successfully!');
        }
      };
      reader.readAsText(file);
    }
  };

  const clearAll = (): void => {
    setInputText('');
    setOutputText('');
    setStats({
      charactersProcessed: 0,
      entitiesFound: 0,
      processingTime: 0,
      operationsCount: 0
    });
    showNotification('Cleared all content!');
  };

  const toggleFullscreen = (): void => {
    setIsFullscreen(!isFullscreen);
  };

  const loadSampleUrls = (): void => {
    setInputText(`https://example.com/search?q=hello world&category=tech&sort=date
https://api.example.com/users/123?fields=name,email,profile&format=json
https://shop.example.com/products?name=iPhone 15 Pro&color=blue&storage=256GB
https://maps.example.com/directions?from=New York, NY&to=Los Angeles, CA
https://social.example.com/posts?tags=programming,javascript&author=john.doe@example.com

# Complex URLs with special characters
https://example.com/path/to/file with spaces.html?param1=value with spaces&param2=50%+discount
https://example.com/api/v1/search?q="hello world"&filters={"type":"article","date":">=2024"}
https://example.com/redirect?url=https://another-site.com/page?id=123&next=/dashboard

# Form data examples
POST data: name=John O'Connor&email=john@example.com&message=Hello! How are you?&rating=5★
Search query: category=electronics&price=<$500&brand=Apple™&model=iPhone®

# International and special characters
https://例え.テスト/パス?クエリ=値
https://example.com/café/résumé?naïve=true&piñata=party
https://example.com/math?equation=a²+b²=c²&symbols=∑∏∫∆

# Common problematic characters
File path: C:\\Users\\John\\Documents\\file name with spaces.txt
Email with plus: john+tag@example.com
URL with fragment: https://example.com/page#section-1
Query with array: items[]=apple&items[]=banana&items[]=cherry`);
  };

  const loadSampleEscaped = (): void => {
    setInputText(`https://example.com/search?q=hello%20world&category=tech&sort=date
https://api.example.com/users/123?fields=name%2Cemail%2Cprofile&format=json
https://shop.example.com/products?name=iPhone%2015%20Pro&color=blue&storage=256GB
https://maps.example.com/directions?from=New%20York%2C%20NY&to=Los%20Angeles%2C%20CA
https://social.example.com/posts?tags=programming%2Cjavascript&author=john.doe%40example.com

%23%20Complex%20URLs%20with%20special%20characters
https://example.com/path/to/file%20with%20spaces.html?param1=value%20with%20spaces&param2=50%25%2Bdiscount
https://example.com/api/v1/search?q=%22hello%20world%22&filters=%7B%22type%22%3A%22article%22%2C%22date%22%3A%22%3E%3D2024%22%7D
https://example.com/redirect?url=https%3A//another-site.com/page%3Fid%3D123&next=%2Fdashboard

%23%20Form%20data%20examples
POST%20data%3A%20name=John%20O%27Connor&email=john%40example.com&message=Hello%21%20How%20are%20you%3F&rating=5%E2%98%85
Search%20query%3A%20category=electronics&price=%3C%24500&brand=Apple%E2%84%A2&model=iPhone%C2%AE

%23%20International%20and%20special%20characters
https://%E4%BE%8B%E3%81%88.%E3%83%86%E3%82%B9%E3%83%88/%E3%83%91%E3%82%B9?%E3%82%AF%E3%82%A8%E3%83%AA=%E5%80%A4
https://example.com/caf%C3%A9/r%C3%A9sum%C3%A9?na%C3%AFve=true&pi%C3%B1ata=party
https://example.com/math?equation=a%C2%B2%2Bb%C2%B2%3Dc%C2%B2&symbols=%E2%88%91%E2%88%8F%E2%88%AB%E2%88%86

%23%20Common%20problematic%20characters
File%20path%3A%20C%3A%5CUsers%5CJohn%5CDocuments%5Cfile%20name%20with%20spaces.txt
Email%20with%20plus%3A%20john%2Btag%40example.com
URL%20with%20fragment%3A%20https%3A//example.com/page%23section-1
Query%20with%20array%3A%20items%5B%5D=apple&items%5B%5D=banana&items%5B%5D=cherry`);
  };

  const parseUrl = (): void => {
    try {
      const lines = inputText.split('\n');
      let parsed = '';
      
      lines.forEach((line, index) => {
        const trimmed = line.trim();
        if (trimmed && !trimmed.startsWith('#') && trimmed.includes('://')) {
          try {
            const url = new URL(trimmed);
            parsed += `URL ${index + 1}:\n`;
            parsed += `  Protocol: ${url.protocol}\n`;
            parsed += `  Host: ${url.hostname}\n`;
            parsed += `  Port: ${url.port || 'default'}\n`;
            parsed += `  Path: ${url.pathname}\n`;
            if (url.search) {
              parsed += `  Query: ${url.search}\n`;
              const params = new URLSearchParams(url.search);
              params.forEach((value, key) => {
                parsed += `    ${key} = ${value}\n`;
              });
            }
            if (url.hash) {
              parsed += `  Fragment: ${url.hash}\n`;
            }
            parsed += '\n';
          } catch (e) {
            parsed += `URL ${index + 1}: Invalid URL format\n\n`;
          }
        }
      });
      
      setOutputText(parsed || 'No valid URLs found to parse');
      showNotification('URLs parsed successfully!');
    } catch (e) {
      showNotification('Error parsing URLs');
    }
  };

  const containerClass = isFullscreen 
    ? "fixed inset-0 z-50 bg-gray-900" 
    : "min-h-screen bg-gradient-to-br from-gray-900 via-teal-900 to-cyan-900";

  return (
    <div className={containerClass}>
      <div className="container mx-auto p-4 h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-r from-teal-500 to-cyan-600 rounded-lg">
              <Globe className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">URL Escape/Unescape Tool</h1>
              <p className="text-gray-300 text-sm">Advanced URL encoder/decoder with international character support</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowAnalytics(!showAnalytics)}
              className="flex items-center space-x-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors"
            >
              <BarChart3 className="w-4 h-4" />
              <span>Analytics</span>
            </button>
            
            <button
              onClick={toggleFullscreen}
              className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
            >
              {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mode Toggle */}
        <div className="flex items-center justify-center mb-6">
          <div className="bg-gray-800 p-1 rounded-lg flex">
            <button
              onClick={() => setMode('escape')}
              className={`px-6 py-2 rounded-md transition-all flex items-center space-x-2 ${
                mode === 'escape' 
                  ? 'bg-teal-600 text-white shadow-lg' 
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              <Zap className="w-4 h-4" />
              <span>Escape URL</span>
            </button>
            <button
              onClick={() => setMode('unescape')}
              className={`px-6 py-2 rounded-md transition-all flex items-center space-x-2 ${
                mode === 'unescape' 
                  ? 'bg-cyan-600 text-white shadow-lg' 
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              <RotateCcw className="w-4 h-4" />
              <span>Unescape URL</span>
            </button>
          </div>
        </div>

        {/* Analytics Panel */}
        {showAnalytics && (
          <div className="mb-6 bg-gray-800 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <BarChart3 className="w-5 h-5 mr-2" />
              Processing Analytics
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-teal-400">{stats.charactersProcessed.toLocaleString()}</div>
                <div className="text-gray-300 text-sm">Characters</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-cyan-400">{stats.entitiesFound}</div>
                <div className="text-gray-300 text-sm">Encoded</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-blue-400">{stats.processingTime.toFixed(2)}ms</div>
                <div className="text-gray-300 text-sm">Process Time</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-green-400">{stats.operationsCount}</div>
                <div className="text-gray-300 text-sm">Operations</div>
              </div>
            </div>
            
            {/* URL Encoding Info */}
            <div className="mt-4 p-3 bg-gray-700 rounded-lg">
              <h4 className="text-white font-medium mb-2">URL Encoding Reference</h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-sm">
                <div className="text-gray-300">
                  <strong>Common:</strong> %20(space) %26(&) %3F(?)
                </div>
                <div className="text-gray-300">
                  <strong>Special:</strong> %2F(/) %3A(:) %23(#)
                </div>
                <div className="text-gray-300">
                  <strong>Reserved:</strong> %21(!) %40(@) %25(%)
                </div>
                <div className="text-gray-300">
                  <strong>Unicode:</strong> UTF-8 sequences
                </div>
                <div className="text-gray-300">
                  <strong>Forms:</strong> + for space
                </div>
                <div className="text-gray-300">
                  <strong>Control:</strong> %00-%1F characters
                </div>
                <div className="text-gray-300">
                  <strong>International:</strong> Multi-byte support
                </div>
                <div className="text-gray-300">
                  <strong>Safe:</strong> RFC 3986 compliant
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content - Large input/output areas */}
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-6 min-h-[600px]">
          {/* Input Section */}
          <div className="bg-gray-800 rounded-lg p-4 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Input</h2>
              <div className="flex space-x-2">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept=".txt,.url,.csv"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <div className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors">
                    <Upload className="w-4 h-4" />
                  </div>
                </label>
                <button
                  onClick={clearAll}
                  className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <textarea
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder={mode === 'escape' ? 'Enter URLs to escape...' : 'Enter escaped URLs to unescape...'}
              className="flex-1 min-h-[500px] bg-gray-900 text-white p-4 rounded-lg border border-gray-600 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none resize-none font-mono text-sm leading-relaxed"
            />
            
            <div className="mt-2 flex justify-between text-gray-400 text-sm">
              <span>Characters: {inputText.length.toLocaleString()}</span>
              <span>Lines: {inputText.split('\n').length}</span>
            </div>
          </div>

          {/* Output Section */}
          <div className="bg-gray-800 rounded-lg p-4 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Output</h2>
              <div className="flex space-x-2">
                <button
                  onClick={copyToClipboard}
                  disabled={!outputText}
                  className="p-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <button
                  onClick={downloadFile}
                  disabled={!outputText}
                  className="p-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  <Download className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <textarea
              value={outputText}
              readOnly
              placeholder="Processed URL output will appear here..."
              className="flex-1 min-h-[500px] bg-gray-900 text-white p-4 rounded-lg border border-gray-600 resize-none font-mono text-sm leading-relaxed"
            />
            
            <div className="mt-2 flex justify-between text-gray-400 text-sm">
              <span>Characters: {outputText.length.toLocaleString()}</span>
              <span>Lines: {outputText.split('\n').length}</span>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-6 bg-gray-800 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-white mb-4">Quick Actions</h3>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={loadSampleUrls}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
            >
              Load Sample URLs
            </button>
            <button
              onClick={loadSampleEscaped}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
            >
              Load Sample Encoded
            </button>
            <button
              onClick={() => setMode(mode === 'escape' ? 'unescape' : 'escape')}
              className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors text-sm"
            >
              Switch Mode
            </button>
            <button
              onClick={parseUrl}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors text-sm"
            >
              Parse URLs
            </button>
            <button
              onClick={() => setInputText(inputText.replace(/\+/g, ' '))}
              className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors text-sm"
            >
              Convert + to Space
            </button>
          </div>
        </div>

        {/* Notification */}
        {notification && (
          <div className="fixed top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2 z-50">
            <CheckCircle2 className="w-4 h-4" />
            <span>{notification}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default UrlEscapeUnescapeTool;